import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Table, Tab, Tabs, Panel, DropdownButton, MenuItem, ButtonGroup, Button } from 'react-bootstrap';
import dayjs from '../util/date.js';

const ActivityDetailsTabs = ({
	activity,
	datasetInfo,
	projectActivities,
	projectActivitiesMap,
	setModalNoteObj,
	activeTab,
	setActiveTab
}) => {
	const formatResource = input => {
		return input.toFixed(2);
	};

	const resources = activity.resourceDetails.length ?
		activity.resourceDetails.map((resource, index) => {
			return (
				<Panel key={`${index}-${resource.activityId}`}>
					<Panel.Heading>
						<Panel.Title toggle>{resource.Resource}</Panel.Title>
					</Panel.Heading>
					<Panel.Collapse>
						<Panel.Body>
							<Table striped>
								<tbody>
									<tr>
										<td>Resource</td>
										<td>{resource.Resource}</td>
									</tr>
									<tr>
										<td>Actual</td>
										<td>{formatResource(resource.Actual)}</td>
									</tr>
									<tr>
										<td>Remaining</td>
										<td>{formatResource(resource.Remaining)}</td>
									</tr>
									<tr>
										<td>Budgeted</td>
										<td>{formatResource(resource.Budgeted)}</td>
									</tr>
								</tbody>
							</Table>
						</Panel.Body>
					</Panel.Collapse>
				</Panel>
			);
		})
		: (<div>This activity has no resources.</div>);

	const isExternalRel = row => row && (row.external === true || row.external === 1 || row.external === '1');
	const relationName = row => (isExternalRel(row) ? `${row.Name} (external)` : row.Name);
	const relationStyle = row => (isExternalRel(row) ? { color: '#6b7280' } : undefined);

	const predecessors = activity.predecessors.length ?
		activity.predecessors.map((predecessor, index) => {
			const external = isExternalRel(predecessor);
			const activityData = !external && (projectActivities[projectActivitiesMap[predecessor.TaskId]] || false);

			const predStart = activityData ?
				dayjs(activityData.start).format('DD-MMM-YYYY') + (activityData.start === activityData.actualStart ? ' A' : '') :
				(predecessor.Start ? dayjs(predecessor.Start).format('DD-MMM-YYYY') + (predecessor.IsActualStart ? ' A' : '') : '—');
			const predFinish = activityData ?
				dayjs(activityData.finish).format('DD-MMM-YYYY') + (activityData.finish === activityData.actualFinish ? ' A' : '') :
				(predecessor.Finish ? dayjs(predecessor.Finish).format('DD-MMM-YYYY') + (predecessor.IsActualFinish ? ' A' : '') : '—');
			const predLabel = relationName(predecessor);

			return (
				<Panel key={`${index}-${predecessor.activityId}`} style={relationStyle(predecessor)}>
					<Panel.Heading>
						<Panel.Title toggle>{predLabel}</Panel.Title>
					</Panel.Heading>
					<Panel.Collapse>
						<Panel.Body>
							<Table striped>
								<tbody>
									<tr>
										<td>Name</td>
										<td>
											{external
												? predLabel
												: <Link className="blueLink" to={`/app/dataset/${datasetInfo.Id}/activity/${predecessor.TaskId}/`}>{predecessor.Name}</Link>}
										</td>
									</tr>
									<tr>
										<td>ID</td>
										<td>{predecessor.ID}</td>
									</tr>
									<tr>
										<td>Type</td>
										<td>{predecessor.Type}</td>
									</tr>
									<tr>
										<td>Lag</td>
										<td>{predecessor.Lag}</td>
									</tr>
									{predecessor.task_type === 'TT_FinMile'
										? null
										: (
											<tr>
												<td>Start</td>
												<td>{predStart}</td>
											</tr>
										)}
									{predecessor.task_type === 'TT_Mile'
										? null
										: (
											<tr>
												<td>Finish</td>
												<td>{predFinish}</td>
											</tr>
										)}
									<tr>
										<td>Float</td>
										<td>{predecessor.Float}</td>
									</tr>
								</tbody>
							</Table>
						</Panel.Body>
					</Panel.Collapse>
				</Panel>
			);
		})
		: (<div>This activity has no predecessors.</div>);

	const successors = activity.successors.length ?
		activity.successors.map((successor, index) => {
			const external = isExternalRel(successor);
			const activityData = !external && (projectActivities[projectActivitiesMap[successor.TaskId]] || false);

			const succStart = activityData ?
				dayjs(activityData.start).format('DD-MMM-YYYY') + (activityData.start === activityData.actualStart ? ' A' : '') :
				(successor.Start ? dayjs(successor.Start).format('DD-MMM-YYYY') + (successor.IsActualStart ? ' A' : '') : '—');
			const succFinish = activityData ?
				dayjs(activityData.finish).format('DD-MMM-YYYY') + (activityData.finish === activityData.actualFinish ? ' A' : '') :
				(successor.Finish ? dayjs(successor.Finish).format('DD-MMM-YYYY') + (successor.IsActualFinish ? ' A' : '') : '—');
			const succLabel = relationName(successor);

			return (
				<Panel key={`${index}-${successor.activityId}`} style={relationStyle(successor)}>
					<Panel.Heading>
						<Panel.Title toggle>{succLabel}</Panel.Title>
					</Panel.Heading>
					<Panel.Collapse>
						<Panel.Body>
							<Table striped>
								<tbody>
									<tr>
										<td>Name</td>
										<td>
											{external
												? succLabel
												: <Link className="blueLink" to={`/app/dataset/${datasetInfo.Id}/activity/${successor.TaskId}/`}>{successor.Name}</Link>}
										</td>
									</tr>
									<tr>
										<td>ID</td>
										<td>{successor.ID}</td>
									</tr>
									<tr>
										<td>Type</td>
										<td>{successor.Type}</td>
									</tr>
									<tr>
										<td>Lag</td>
										<td>{successor.Lag}</td>
									</tr>
									{successor.task_type === 'TT_FinMile'
										? null
										: (
											<tr>
												<td>Start</td>
												<td>{succStart}</td>
											</tr>
										)}
									{successor.task_type === 'TT_Mile'
										? null
										: (
											<tr>
												<td>Finish</td>
												<td>{succFinish}</td>
											</tr>
										)}
									<tr>
										<td>Float</td>
										<td>{successor.Float}</td>
									</tr>
								</tbody>
							</Table>
						</Panel.Body>
					</Panel.Collapse>
				</Panel>
			);
		})
		: (<div>This activity has no successors.</div>);

	const notes = activity.notes.length ?
		activity.notes.map((note, index) => {
			return (
				<Panel key={`${index}-${note.noteId}`}>
					<Panel.Heading>
						<Panel.Title toggle>{note.type}</Panel.Title>
					</Panel.Heading>
					<Panel.Collapse>
						<Panel.Body>
							<ButtonGroup>
								<Button
									onClick={() => setModalNoteObj(note)}>
									<span className="glyphicon glyphicon-pencil leftGlyph"></span>
									Edit
								</Button>
								{/*<Button>
									<span className="glyphicon glyphicon-trash leftGlyph"></span>
									Delete
								</Button>*/}
							</ButtonGroup>
							<br /><br />
							{note.note}
						</Panel.Body>
					</Panel.Collapse>
				</Panel>
			);
		})
		: (<div>This activity has no notes.</div>);

	const notesIds = activity.notes.length ?
		activity.notes.map((note, index) => {
			return note.typeId;
		})
		: [];

	const noteTypes = datasetInfo.memoTypes.length
		? (
			<DropdownButton title="Add Note" id="memoTypeDropDown">
				{datasetInfo.memoTypes.map((memoType, index) => {
					return (
						<MenuItem
							key={index}
							eventKey={memoType.memo_type_id}
							disabled={notesIds.indexOf(memoType.memo_type_id) > -1}
							onSelect={() => {
								setModalNoteObj({
									activityId: activity.activityId,
									noteId: '',
									typeId: memoType.memo_type_id,
									type: memoType.memo_type,
									note: ''
								});
							}}>
							{memoType.memo_type}
						</MenuItem>
					);
				})}
			</DropdownButton>
		)
		: null;

	let codes = [];
	for (const i in activity.properties) {
		const code = activity.properties[i];
		codes.push(
			<tr key={i}>
				<td>{code.propertyName}</td>
				<td>{code.value}</td>
			</tr>
		);
	}
	if (codes.length) {
		codes = <Table striped><tbody>{codes}</tbody></Table>;
	} else {
		codes = (<div>This activity has no codes</div>);
	}

	return (
		<div className="activityProgressDetails">
			<Tabs
				id="activityDetailsTabs"
				activeKey={activeTab}
				onSelect={setActiveTab}
				animation={false}
				mountOnEnter={false}
				unmountOnExit={false}
				aria-label="Activity details">
				<Tab
					eventKey={1}
					className="activityDetailsTabs resources"
					title={<span>
						<span className="glyphicon glyphicon-user leftGlyph" aria-hidden="true"></span>
						<span className="tabName">Resources</span>
					</span>}>
					<h3 className="activityDetailsTabTitle">Resources</h3>
					{resources}
				</Tab>
				<Tab
					eventKey={2}
					className="activityDetailsTabs predecessors"
					title={<span>
						<span className="glyphicon glyphicon-arrow-left leftGlyph" aria-hidden="true"></span>
						<span className="tabName">Predecessors</span>
					</span>}>
					<h3 className="activityDetailsTabTitle">Predecessors</h3>
					{predecessors}
				</Tab>
				<Tab
					eventKey={3}
					className="activityDetailsTabs successors"
					title={<span>
						<span className="glyphicon glyphicon-arrow-right leftGlyph" aria-hidden="true"></span>
						<span className="tabName">Successors</span>
					</span>}>
					<h3 className="activityDetailsTabTitle">Successors</h3>
					{successors}
				</Tab>
				<Tab
					eventKey={4}
					className="activityDetailsTabs notes"
					title={<span>
						<span className="glyphicon glyphicon-comment leftGlyph" aria-hidden="true"></span>
						<span className="tabName">Notes</span>
					</span>}>
					<h3 className="activityDetailsTabTitle">Notes</h3>
					{noteTypes}
					{notes}
				</Tab>
				<Tab
					eventKey={5}
					className="activityDetailsTabs"
					title={<span>
						<span className="glyphicon glyphicon-info-sign leftGlyph" aria-hidden="true"></span>
						<span className="tabName">Codes</span>
					</span>}>
					<h3 className="activityDetailsTabTitle">Codes</h3>
					{codes}
				</Tab>
			</Tabs>
		</div>
	);
};

ActivityDetailsTabs.propTypes = {
	activity: PropTypes.object.isRequired,
	datasetInfo: PropTypes.object.isRequired,
	projectActivities: PropTypes.array,
	projectActivitiesMap: PropTypes.object,
	setModalNoteObj: PropTypes.func,
	activeTab: PropTypes.number,
	setActiveTab: PropTypes.func
};

export default ActivityDetailsTabs;